/**
 * Registration Page
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import AppleSignin from 'react-apple-signin-auth';
import { useAuth } from '../../context/AuthContext';
import { Eye, EyeOff, AlertCircle, Ship, Users, Anchor, X } from 'lucide-react';
import CountdownTimer, { isFreePeriod } from '../../components/CountdownTimer';
import SEOHead from '../../components/SEOHead';
import { PAGE_SEO } from '../../utils/seoConstants';

// Terms and Conditions Modal Component
function TermsModal({ isOpen, onClose }) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-navy-900">Terms and Conditions</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-navy-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 text-sm text-navy-700 space-y-4">
          <p className="font-semibold">Effective Date: 24 March 2026 — www.1crew3.com</p>

          <p className="uppercase font-semibold text-navy-900">
            PLEASE READ THESE TERMS AND CONDITIONS CAREFULLY BEFORE USING THE PLATFORM. BY REGISTERING FOR AN ACCOUNT OR USING ANY PART OF THE 1CREW3 PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND CONDITIONS IN THEIR ENTIRETY. IF YOU DO NOT AGREE, DO NOT USE THE PLATFORM.
          </p>

          <p>
            1crew3 is a global crew connection platform that originated from AnchorPals, a service created to address the lack of available mooring assistance for late arrivals in ports and marinas. 1crew3 has since evolved into a platform connecting yacht crew with captains and yacht owners worldwide. The Platform is free for crew members. 1crew3 acts solely as an introductory service and does not employ, manage, or supervise any crew.
          </p>

          <h3 className="font-bold text-navy-900 mt-6">1. Definitions</h3>
          <p>"1crew3", "we", "us", or "our" refers to 1crew3, the trading name of the entity operating the website 1crew3.com and all associated services.</p>
          <p>"Platform" refers to the website 1crew3.com, any associated mobile applications, and all related tools and services.</p>
          <p>"User", "you", or "your" refers to any individual or entity who accesses, registers on, or uses the Platform, whether as a crew member, captain, yacht owner, management company, or any other capacity.</p>
          <p>"Crew Member" refers to any individual registered on the Platform who is seeking or offering services in the yachting or maritime industry.</p>
          <p>"Client" refers to any captain, yacht owner, yacht management company, or other party seeking to engage crew through the Platform.</p>
          <p>"Services" refers to the introductory and connection services provided by 1crew3, limited to facilitating initial contact between Crew Members and Clients.</p>
          <p>"Mooring Assistance" refers to any physical assistance with mooring, docking, line handling, or related harbour operations facilitated through the Platform or its predecessor service AnchorPals.</p>

          <h3 className="font-bold text-navy-900 mt-6">2. Nature of Services</h3>
          <p>2.1. 1crew3 operates exclusively as an introductory platform. We connect Crew Members with Clients. We do not act as an employer, employment agency, manning agent, recruitment agency, staffing company, contractor, or maritime labour provider under any applicable law or convention.</p>
          <p>2.2. 1crew3 does not employ, engage, supervise, direct, manage, or control any Crew Member or any work performed by any Crew Member at any time.</p>
          <p>2.3. Any working relationship, contract of employment, freelance arrangement, or engagement of any kind that results from an introduction made through 1crew3 is entered into solely and exclusively between the Crew Member and the Client. 1crew3 is not a party to any such arrangement.</p>
          <p>2.4. 1crew3 does not guarantee, promise, or imply that any introduction will result in employment, engagement, or any working arrangement.</p>

          <h3 className="font-bold text-navy-900 mt-6">3. No Verification or Vetting</h3>
          <p className="uppercase font-semibold">3.1. 1CREW3 DOES NOT VERIFY, CHECK, VALIDATE, CONFIRM, OR AUTHENTICATE ANY INFORMATION PROVIDED BY ANY USER ON THE PLATFORM. This includes but is not limited to:</p>
          <p>(a) Qualifications, certifications, licences, or endorsements (including but not limited to STCW certificates, ENG1 medicals, yacht master qualifications, powerboat licences, food safety certificates, or any other maritime or professional certification);</p>
          <p>(b) Training records, sea time, or experience claims;</p>
          <p>(c) References, recommendations, testimonials, or employment history;</p>
          <p>(d) Identity documents, right to work status, visa status, or immigration status;</p>
          <p>(e) Criminal background, disciplinary history, or flag state records;</p>
          <p>(f) Medical fitness, physical capability, drug and alcohol test results, or mental health status;</p>
          <p>(g) Insurance status, including personal liability, professional indemnity, or any other coverage;</p>
          <p>(h) Skills, competencies, language abilities, or any other claimed attributes.</p>
          <p className="uppercase font-semibold">3.2. IT IS THE SOLE AND EXCLUSIVE RESPONSIBILITY OF THE CLIENT (captain, owner, or management company) TO INDEPENDENTLY VERIFY all qualifications, certifications, references, identity, right to work, medical fitness, background, and suitability of any Crew Member before engaging them in any capacity.</p>
          <p>3.3. 1crew3 makes no representation or warranty whatsoever, whether express, implied, statutory, or otherwise, regarding the accuracy, completeness, truthfulness, reliability, or validity of any information, profile, qualification, reference, or statement made by any User on the Platform.</p>

          <h3 className="font-bold text-navy-900 mt-6">4. Assumption of Risk</h3>
          <p>4.1. You acknowledge and agree that the maritime and yachting industry involves inherent and significant risks, including but not limited to risks of personal injury, death, property damage, loss of property, and environmental damage.</p>
          <p>4.2. By using the Platform, you voluntarily assume all risks associated with:</p>
          <p>(a) Any mooring, docking, line handling, anchoring, or harbour operation;</p>
          <p>(b) Any work performed aboard or in connection with any vessel;</p>
          <p>(c) Any interaction, meeting, communication, or engagement with any other User;</p>
          <p>(d) Any travel to or from any vessel, marina, port, or location;</p>
          <p>(e) Any work performed at height, near water, with heavy equipment, machinery, or hazardous materials;</p>
          <p>(f) Weather conditions, sea states, tidal conditions, or any natural phenomena;</p>
          <p>(g) The actions, omissions, negligence, recklessness, or wilful misconduct of any other User or third party.</p>
          <p>4.3. 1crew3 has no control over and accepts no responsibility for the condition, seaworthiness, or safety of any vessel, equipment, marina, dock, harbour, or any other location.</p>

          <h3 className="font-bold text-navy-900 mt-6">5. Limitation of Liability</h3>
          <p className="uppercase font-semibold">5.1. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, 1CREW3, ITS OWNERS, DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, AFFILIATES, SUCCESSORS, AND ASSIGNS SHALL NOT BE LIABLE FOR ANY:</p>
          <p>(a) Death, personal injury, or bodily harm of any kind;</p>
          <p>(b) Damage to or loss of any vessel, property, equipment, or cargo;</p>
          <p>(c) Environmental damage, pollution, or contamination;</p>
          <p>(d) Financial loss, loss of earnings, loss of profit, loss of business, loss of contract, or loss of opportunity;</p>
          <p>(e) Consequential, incidental, indirect, special, punitive, or exemplary damages;</p>
          <p>(f) Reputational damage;</p>
          <p>(g) Costs of legal proceedings, fines, penalties, or regulatory action;</p>
          <p>(h) Any loss or damage arising from the conduct, negligence, incompetence, dishonesty, fraud, theft, criminal behaviour, or misconduct of any User;</p>
          <p>(i) Any loss or damage arising from inaccurate, false, misleading, or fraudulent information provided by any User;</p>
          <p>(j) Any loss or damage arising from the failure of any User to hold valid qualifications, certifications, insurance, or any required documentation;</p>
          <p>(k) Any loss or damage of any kind whatsoever, whether foreseeable or not, and whether arising in contract, tort (including negligence), strict liability, or otherwise.</p>
          <p>5.2. If, notwithstanding the above, 1crew3 is found liable for any claim, our total aggregate liability shall not exceed the amount of fees paid by you to 1crew3 in the twelve (12) months preceding the event giving rise to the claim, or EUR 100 (one hundred euros), whichever is less.</p>

          <h3 className="font-bold text-navy-900 mt-6">6. Indemnification</h3>
          <p>6.1. You agree to indemnify, defend, and hold harmless 1crew3, its owners, directors, officers, employees, agents, affiliates, successors, and assigns from and against any and all claims, demands, actions, proceedings, liabilities, damages, losses, costs, and expenses (including reasonable legal fees) arising out of or in connection with:</p>
          <p>(a) Your use of the Platform;</p>
          <p>(b) Any content, information, or material you submit, post, or transmit through the Platform;</p>
          <p>(c) Any arrangement, engagement, or relationship entered into as a result of the Platform;</p>
          <p>(d) Any breach of these Terms and Conditions;</p>
          <p>(e) Any violation of applicable law, regulation, or convention;</p>
          <p>(f) Any claim by a third party arising from your conduct, negligence, or breach of duty;</p>
          <p>(g) Any personal injury, death, or property damage occurring in connection with any service, work, or activity facilitated by the Platform.</p>

          <h3 className="font-bold text-navy-900 mt-6">7. Mooring and Physical Assistance</h3>
          <p>7.1. Where the Platform facilitates the connection of individuals offering or seeking mooring assistance, late-arrival mooring, line handling, or any other physical assistance:</p>
          <p>(a) 1crew3 acts solely as a communication platform connecting parties;</p>
          <p>(b) 1crew3 does not supervise, direct, instruct, or oversee any mooring or physical operation;</p>
          <p>(c) 1crew3 does not assess the competence, fitness, or suitability of any individual to perform mooring or physical tasks;</p>
          <p>(d) All parties involved in any mooring or physical assistance operation do so entirely at their own risk;</p>
          <p>(e) The captain or person in charge of the vessel retains full and sole responsibility for the safety of all operations at all times, as required by maritime law;</p>
          <p>(f) 1crew3 accepts no liability whatsoever for any accident, injury, death, damage, or loss arising from any mooring or physical assistance activity.</p>

          <h3 className="font-bold text-navy-900 mt-6">8. User Conduct and Responsibilities</h3>
          <p>8.1. You represent and warrant that all information you provide on the Platform is accurate, complete, current, and not misleading.</p>
          <p>8.2. You are solely responsible for maintaining the confidentiality of your account credentials.</p>
          <p>8.3. You agree not to use the Platform for any unlawful, fraudulent, harmful, or abusive purpose.</p>
          <p>8.4. You agree not to harass, threaten, intimidate, discriminate against, or behave inappropriately towards any other User.</p>
          <p>8.5. 1crew3 is not responsible for the behaviour, conduct, actions, or omissions of any User, whether on or off the Platform. Any dispute between Users shall be resolved between those Users directly. 1crew3 shall not be a party to any such dispute.</p>
          <p>8.6. 1crew3 reserves the right to suspend or terminate any User account at its sole discretion, without notice and without liability, for any reason or no reason.</p>

          <h3 className="font-bold text-navy-900 mt-6">9. No Employment Relationship</h3>
          <p>9.1. Nothing in these Terms, and no use of the Platform, shall create or be deemed to create any employment relationship, partnership, joint venture, agency, or fiduciary relationship between 1crew3 and any User.</p>
          <p>9.2. Crew Members are not employees, agents, or representatives of 1crew3 under any circumstances.</p>
          <p>9.3. 1crew3 has no obligation to provide work, pay wages, provide benefits, withhold taxes, or fulfil any employer obligation whatsoever to any User.</p>
          <p>9.4. 1crew3 is not subject to the Maritime Labour Convention (MLC) 2006, or any other labour convention or regulation, in respect of any Crew Member registered on the Platform.</p>

          <h3 className="font-bold text-navy-900 mt-6">10. Insurance</h3>
          <p>10.1. 1crew3 does not provide, arrange, or verify any insurance coverage for any User.</p>
          <p>10.2. All Users are strongly advised to obtain their own appropriate insurance, including but not limited to personal liability insurance, professional indemnity insurance, medical insurance, travel insurance, and any other coverage appropriate to their activities.</p>
          <p>10.3. 1crew3 accepts no liability for any loss arising from a User's failure to obtain adequate insurance.</p>

          <h3 className="font-bold text-navy-900 mt-6">11. Intellectual Property</h3>
          <p>11.1. All content, trademarks, logos, and intellectual property on the Platform belong to 1crew3 or its licensors.</p>
          <p>11.2. Users may not copy, reproduce, distribute, or create derivative works from Platform content without prior written consent.</p>

          <h3 className="font-bold text-navy-900 mt-6">12. Data Protection and Privacy</h3>
          <p>12.1. 1crew3 processes personal data in accordance with our Privacy Policy, which forms part of these Terms.</p>
          <p>12.2. By registering, you consent to the collection, processing, and storage of your personal data as described in the Privacy Policy.</p>
          <p>12.3. 1crew3 shall not be liable for any unauthorised access to User data resulting from circumstances beyond our reasonable control.</p>

          <h3 className="font-bold text-navy-900 mt-6">13. Free Service for Crew Members</h3>
          <p>13.1. The Platform is currently offered free of charge to Crew Members. 1crew3 reserves the right to introduce fees or charges at any time, with reasonable notice.</p>
          <p>13.2. The free nature of the service does not create any obligation on 1crew3 to maintain, continue, or improve the Platform or any aspect of it.</p>

          <h3 className="font-bold text-navy-900 mt-6">14. Platform Availability</h3>
          <p>14.1. 1crew3 does not guarantee that the Platform will be available at all times, uninterrupted, error-free, or secure.</p>
          <p>14.2. 1crew3 may modify, suspend, or discontinue the Platform or any part of it at any time without notice or liability.</p>

          <h3 className="font-bold text-navy-900 mt-6">15. Third-Party Links and Content</h3>
          <p>15.1. The Platform may contain links to third-party websites or services. 1crew3 does not endorse, control, or accept responsibility for any third-party content or services.</p>

          <h3 className="font-bold text-navy-900 mt-6">16. Dispute Resolution</h3>
          <p>16.1. Any dispute arising out of or in connection with these Terms shall first be attempted to be resolved through good faith negotiation.</p>
          <p>16.2. If negotiation fails, any dispute shall be submitted to mediation before a mutually agreed mediator.</p>
          <p>16.3. If mediation fails, the dispute shall be submitted to the exclusive jurisdiction of the courts as specified in the Governing Law section below.</p>

          <h3 className="font-bold text-navy-900 mt-6">17. Governing Law and Jurisdiction</h3>
          <p>17.1. These Terms and Conditions shall be governed by and construed in accordance with the laws of France.</p>
          <p>17.2. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts of Antibes, France.</p>
          <p>17.3. If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.</p>

          <h3 className="font-bold text-navy-900 mt-6">18. Force Majeure</h3>
          <p>18.1. 1crew3 shall not be liable for any failure or delay in performing its obligations where such failure or delay results from circumstances beyond its reasonable control.</p>

          <h3 className="font-bold text-navy-900 mt-6">19. Modifications to Terms</h3>
          <p>19.1. 1crew3 reserves the right to modify these Terms and Conditions at any time. Updated Terms will be posted on the Platform with a revised effective date.</p>
          <p>19.2. Continued use of the Platform after any modification constitutes acceptance of the updated Terms.</p>

          <h3 className="font-bold text-navy-900 mt-6">20. Entire Agreement</h3>
          <p>20.1. These Terms and Conditions, together with the Privacy Policy, constitute the entire agreement between you and 1crew3 regarding your use of the Platform.</p>

          <h3 className="font-bold text-navy-900 mt-6">21. Waiver</h3>
          <p>21.1. No failure or delay by 1crew3 in exercising any right under these Terms shall constitute a waiver of that right.</p>

          <h3 className="font-bold text-navy-900 mt-6">22. Contact</h3>
          <p>22.1. For any questions regarding these Terms and Conditions, please contact us through the contact form at 1crew3.com.</p>

          <p className="uppercase font-bold text-navy-900 mt-6">
            BY CLICKING "I AGREE", "ACCEPT", OR BY REGISTERING FOR AN ACCOUNT ON 1CREW3.COM, YOU CONFIRM THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND CONDITIONS.
          </p>
        </div>
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full btn-primary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Terms Checkbox Component
function TermsCheckbox({ checked, onChange, onOpenTerms }) {
  return (
    <label className="flex items-start cursor-pointer py-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-5 w-5 mt-0.5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
      />
      <span className="ml-3 text-sm text-navy-700">
        I have read and agree to the{' '}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); onOpenTerms(); }}
          className="text-primary-600 hover:text-primary-700 font-medium underline"
        >
          Terms and Conditions
        </button>
      </span>
    </label>
  );
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Separate component for Google login to isolate the hook
function GoogleLoginButtonInternal({ label, onSuccess, onError, disabled }) {
  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    onSuccess,
    onError: (errorResponse) => {
      console.error('Google OAuth error:', errorResponse);
      onError(errorResponse?.error_description || 'Google sign-in failed. Please try again.');
    },
    onNonOAuthError: (error) => {
      console.error('Google non-OAuth error:', error);
      if (error?.type === 'popup_closed') {
        onError('Google sign-in was cancelled.');
      } else {
        onError('Google sign-in could not load. Please check your browser settings and try again.');
      }
    },
  });

  return (
    <button
      type="button"
      onClick={() => googleLogin()}
      disabled={disabled}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-navy-200 rounded-apple-lg hover:bg-navy-50 transition-all duration-300 disabled:opacity-50"
    >
      <svg width="20" height="20" viewBox="0 0 24 24">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      <span className="text-sm font-medium text-navy-700">{label || 'Continue with Google'}</span>
    </button>
  );
}

// Constants for helper position/certification data
const DEPARTMENTS = [
  { value: 'deck', label: 'Deck' },
  { value: 'interior', label: 'Interior' },
  { value: 'galley', label: 'Galley' },
  { value: 'engineering', label: 'Engineering' },
];

const POSITIONS = {
  deck: [
    { value: 'deck_greenie', label: 'Greenie (Green Deckhand)' },
    { value: 'deck_deckhand', label: 'Deckhand' },
    { value: 'deck_bosun', label: 'Bosun' },
    { value: 'deck_first_mate', label: 'First Mate / Chief Officer' },
    { value: 'deck_captain', label: 'Captain / Master' },
  ],
  interior: [
    { value: 'interior_junior_stew', label: 'Junior Stewardess' },
    { value: 'interior_stew', label: 'Stewardess / Steward' },
    { value: 'interior_2nd_stew', label: '2nd Stewardess' },
    { value: 'interior_chief_stew', label: 'Chief Stewardess' },
    { value: 'interior_purser', label: 'Purser' },
  ],
  galley: [
    { value: 'galley_junior_chef', label: 'Junior Chef / Cook' },
    { value: 'galley_sole_chef', label: 'Sole Chef / Chef' },
    { value: 'galley_head_chef', label: 'Head Chef' },
  ],
  engineering: [
    { value: 'eng_junior_engineer', label: 'Junior Engineer / Wiper' },
    { value: 'eng_2nd_engineer', label: '2nd Engineer / ETO' },
    { value: 'eng_chief_engineer', label: 'Chief Engineer' },
  ],
};

const CERTIFICATION_GROUPS = [
  {
    label: 'Safety & Medical',
    certs: [
      { id: 'cert_stcw_bst', label: 'STCW Basic Safety Training (BST)' },
      { id: 'cert_eng1', label: 'ENG1 Medical Certificate' },
      { id: 'cert_adv_fire', label: 'Advanced Fire Fighting' },
      { id: 'cert_pscrb', label: 'Proficiency in Survival Craft (PSCRB)' },
      { id: 'cert_medical_care', label: 'Medical Care (STCW A-VI/4)' },
      { id: 'cert_sso', label: 'Ship Security Officer (SSO)' },
      { id: 'cert_ism_isps', label: 'ISM & ISPS Familiarisation' },
    ],
  },
  {
    label: 'Deck & Navigation',
    certs: [
      { id: 'cert_powerboat_l2', label: 'Powerboat Level 2' },
      { id: 'cert_ym_coastal', label: 'Yachtmaster Coastal' },
      { id: 'cert_ym_offshore', label: 'Yachtmaster Offshore' },
      { id: 'cert_ym_ocean', label: 'Yachtmaster Ocean' },
      { id: 'cert_oow_3000', label: 'Officer of the Watch (≤3000 GT)' },
      { id: 'cert_master_3000', label: 'Master (≤3000 GT)' },
      { id: 'cert_master_200', label: 'Master 200 GT' },
      { id: 'cert_gmdss_goc', label: 'GMDSS General Operator Certificate' },
      { id: 'cert_brm', label: 'Bridge Resource Management' },
      { id: 'cert_ecdis', label: 'ECDIS (Electronic Chart Display)' },
      { id: 'cert_dp', label: 'Dynamic Positioning' },
    ],
  },
  {
    label: 'Interior & Hospitality',
    certs: [
      { id: 'cert_food_safety_l2', label: 'Food Safety Level 2' },
      { id: 'cert_food_safety_l3', label: 'Food Safety Level 3' },
      { id: 'cert_wset_l1', label: 'WSET Level 1 (Wines)' },
      { id: 'cert_wset_l2', label: 'WSET Level 2 (Wines)' },
      { id: 'cert_wset_l3', label: 'WSET Level 3 (Wines)' },
    ],
  },
  {
    label: 'Galley / Chef',
    certs: [
      { id: 'cert_ships_cook', label: "Ship's Cook Certificate" },
    ],
  },
  {
    label: 'Engineering',
    certs: [
      { id: 'cert_aec', label: 'Approved Engine Course (AEC)' },
      { id: 'cert_y4', label: 'Yacht Engineer Y4' },
      { id: 'cert_y3', label: 'Yacht Engineer Y3' },
      { id: 'cert_y2', label: 'Yacht Engineer Y2' },
      { id: 'cert_y1', label: 'Yacht Engineer Y1' },
      { id: 'cert_eto', label: 'Electro-Technical Officer (ETO)' },
    ],
  },
  {
    label: 'Water Sports & Other',
    certs: [
      { id: 'cert_padi_ow', label: 'PADI Open Water Diver' },
      { id: 'cert_padi_dm', label: 'PADI Divemaster' },
      { id: 'cert_rya_pwc', label: 'RYA Jet Ski License (PWC)' },
    ],
  },
];

// Which cert groups are relevant for each department
// Safety & Medical is universal, then department-specific groups
const DEPARTMENT_CERT_GROUPS = {
  deck: ['Safety & Medical', 'Deck & Navigation', 'Water Sports & Other'],
  interior: ['Safety & Medical', 'Interior & Hospitality'],
  galley: ['Safety & Medical', 'Galley / Chef', 'Interior & Hospitality'],
  engineering: ['Safety & Medical', 'Engineering'],
};

// Which specific certs are expected/relevant for each position
// Used to highlight which certs the helper should have for their role
const POSITION_EXPECTED_CERTS = {
  deck_greenie: ['cert_stcw_bst', 'cert_eng1'],
  deck_deckhand: ['cert_stcw_bst', 'cert_eng1', 'cert_powerboat_l2'],
  deck_bosun: ['cert_stcw_bst', 'cert_eng1', 'cert_powerboat_l2', 'cert_ym_coastal'],
  deck_first_mate: ['cert_stcw_bst', 'cert_eng1', 'cert_oow_3000', 'cert_gmdss_goc', 'cert_adv_fire', 'cert_medical_care', 'cert_pscrb', 'cert_brm', 'cert_ecdis'],
  deck_captain: ['cert_stcw_bst', 'cert_eng1', 'cert_master_3000', 'cert_gmdss_goc', 'cert_adv_fire', 'cert_medical_care', 'cert_pscrb', 'cert_sso', 'cert_ism_isps', 'cert_brm', 'cert_ecdis'],
  interior_junior_stew: ['cert_stcw_bst', 'cert_eng1'],
  interior_stew: ['cert_stcw_bst', 'cert_eng1', 'cert_food_safety_l2'],
  interior_2nd_stew: ['cert_stcw_bst', 'cert_eng1', 'cert_food_safety_l2', 'cert_wset_l1'],
  interior_chief_stew: ['cert_stcw_bst', 'cert_eng1', 'cert_food_safety_l2', 'cert_wset_l2'],
  interior_purser: ['cert_stcw_bst', 'cert_eng1', 'cert_food_safety_l2'],
  galley_junior_chef: ['cert_stcw_bst', 'cert_eng1', 'cert_ships_cook', 'cert_food_safety_l2'],
  galley_sole_chef: ['cert_stcw_bst', 'cert_eng1', 'cert_ships_cook', 'cert_food_safety_l2'],
  galley_head_chef: ['cert_stcw_bst', 'cert_eng1', 'cert_ships_cook', 'cert_food_safety_l3'],
  eng_junior_engineer: ['cert_stcw_bst', 'cert_eng1', 'cert_aec'],
  eng_2nd_engineer: ['cert_stcw_bst', 'cert_eng1', 'cert_aec', 'cert_y4', 'cert_y3'],
  eng_chief_engineer: ['cert_stcw_bst', 'cert_eng1', 'cert_adv_fire', 'cert_pscrb', 'cert_y2', 'cert_y1'],
};

const VESSEL_SIZES = [
  { value: 'size_under_30m', label: 'Under 30m' },
  { value: 'size_30_50m', label: '30–50m' },
  { value: 'size_50_80m', label: '50–80m' },
  { value: 'size_over_80m', label: '80m+' },
];

const LANGUAGES = [
  { value: 'lang_english', label: 'English' },
  { value: 'lang_french', label: 'French' },
  { value: 'lang_spanish', label: 'Spanish' },
  { value: 'lang_italian', label: 'Italian' },
  { value: 'lang_german', label: 'German' },
  { value: 'lang_dutch', label: 'Dutch' },
  { value: 'lang_russian', label: 'Russian' },
];

function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, socialLogin, error, clearError } = useAuth();

  const typeParam = searchParams.get('type');
  const initialUserType = typeParam === 'owner' ? 'yacht_owner' : (typeParam === 'helper' ? 'helper' : '');
  const [step, setStep] = useState(initialUserType ? 2 : 1);
  const [formData, setFormData] = useState({
    user_type: initialUserType,
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    use_whatsapp: false,
    yacht_name: '',
    yacht_type: 'motor',
    yacht_length: '',
    length_unit: 'meters',
    home_port: '',
    // Helper profile fields
    department: '',
    position: '',
    experience_years: '',
    certifications: {},
    available_from: '',
    preferred_sizes: [],
    languages_spoken: [],
    date_of_birth: '',
    nationality: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Terms and Conditions state
  const [tcAccepted, setTcAccepted] = useState(false);
  const [showTcModal, setShowTcModal] = useState(false);

  // Social login state
  const [socialData, setSocialData] = useState(null); // { provider, token, email, first_name, last_name }

  const handleChange = (e) => {
    clearError();
    setValidationError('');
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleCertification = (certId) => {
    setFormData(prev => ({
      ...prev,
      certifications: {
        ...prev.certifications,
        [certId]: !prev.certifications[certId]
      }
    }));
  };

  const toggleArrayItem = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleUserTypeSelect = (type) => {
    setFormData({ ...formData, user_type: type });
    setStep(socialData ? 'social-phone' : 2);
  };

  // Google login success handler
  const handleGoogleSuccess = async (tokenResponse) => {
    setLoading(true);
    setValidationError('');
    try {
      const data = await socialLogin('google', tokenResponse.access_token, {});

      if (data.needs_completion) {
        setSocialData({
          provider: 'google',
          token: tokenResponse.access_token,
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
        });
        setFormData(prev => ({
          ...prev,
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
        }));
        if (formData.user_type) {
          setStep('social-phone');
        } else {
          setStep(1);
        }
      } else {
        const dashboardPath = data.user.user_type === 'helper' ? '/helper' : '/owner';
        navigate(dashboardPath);
      }
    } catch (err) {
      setValidationError(err.message || 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Apple auth config
  const appleAuthOptions = {
    clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
    scope: 'email name',
    redirectURI: window.location.origin + '/auth/apple/callback',
    usePopup: true,
  };

  // Apple login success handler
  const handleAppleSuccess = async (response) => {
    if (!response?.authorization) return;

    setLoading(true);
    setValidationError('');
    try {
      const idToken = response.authorization.id_token;
      const firstName = response.user?.name?.firstName || '';
      const lastName = response.user?.name?.lastName || '';

      const data = await socialLogin('apple', idToken, {
        first_name: firstName,
        last_name: lastName,
      });

      if (data.needs_completion) {
        setSocialData({
          provider: 'apple',
          token: idToken,
          email: data.email,
          first_name: data.first_name || firstName,
          last_name: data.last_name || lastName,
        });
        setFormData(prev => ({
          ...prev,
          email: data.email,
          first_name: data.first_name || firstName,
          last_name: data.last_name || lastName,
        }));
        if (formData.user_type) {
          setStep('social-phone');
        } else {
          setStep(1);
        }
      } else {
        const dashboardPath = data.user.user_type === 'helper' ? '/helper' : '/owner';
        navigate(dashboardPath);
      }
    } catch (err) {
      setValidationError(err.message || 'Apple sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Apple button component
  const AppleButton = ({ label }) => {
    if (!process.env.REACT_APP_APPLE_CLIENT_ID) return null;
    return (
      <AppleSignin
        authOptions={appleAuthOptions}
        onSuccess={handleAppleSuccess}
        onError={() => setValidationError('Apple sign-in failed')}
        noDefaultStyle
        render={(renderProps) => (
          <button
            onClick={renderProps.onClick}
            type="button"
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-black rounded-apple-lg hover:bg-gray-900 transition-all duration-300 disabled:opacity-50 mt-3"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            <span className="text-sm font-medium text-white">{label || 'Continue with Apple'}</span>
          </button>
        )}
      />
    );
  };

  // Submit social registration (user_type + phone collected)
  const handleSocialComplete = async (e) => {
    e.preventDefault();
    if (!formData.phone.trim()) {
      setValidationError('Phone number is required');
      return;
    }

    // If helper, go to profile step first
    if (formData.user_type === 'helper' && !formData.department) {
      setStep(3);
      return;
    }

    if (!tcAccepted) {
      setValidationError('You must accept the Terms and Conditions to register');
      return;
    }

    setLoading(true);
    try {
      const data = await socialLogin(socialData.provider, socialData.token, {
        user_type: formData.user_type,
        phone: formData.phone,
        use_whatsapp: formData.use_whatsapp,
        first_name: socialData.first_name,
        last_name: socialData.last_name,
        // T&C acceptance data
        tc_accepted: true,
        tc_accepted_at: new Date().toISOString(),
        tc_version: '2026-03-24',
        // Helper fields
        ...(formData.user_type === 'helper' ? {
          department: formData.department,
          position: formData.position,
          experience_years: parseInt(formData.experience_years) || 0,
          certifications: formData.certifications,
          available_from: formData.available_from || null,
          preferred_sizes: formData.preferred_sizes,
          languages_spoken: formData.languages_spoken,
          date_of_birth: formData.date_of_birth || null,
          nationality: formData.nationality || null,
        } : {}),
        // Yacht details if yacht owner
        ...(formData.user_type === 'yacht_owner' && formData.yacht_name ? {
          yacht_name: formData.yacht_name,
          yacht_type: formData.yacht_type,
          yacht_length: formData.length_unit === 'feet'
            ? parseFloat(formData.yacht_length) * 0.3048
            : parseFloat(formData.yacht_length),
        } : {}),
      });

      if (data.user) {
        const dashboardPath = data.user.user_type === 'helper' ? '/helper' : '/owner';
        navigate(dashboardPath);
      }
    } catch (err) {
      // Error handled by AuthContext
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSubmit = (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setValidationError('Password must be at least 8 characters');
      return;
    }

    if (!formData.phone.trim()) {
      setValidationError('Phone number is required');
      return;
    }

    // Both owners and helpers go to step 3
    setStep(3);
  };

  const handleFinalSubmit = async () => {
    if (!tcAccepted) {
      setValidationError('You must accept the Terms and Conditions to register');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, length_unit, ...registerData } = formData;

      if (formData.user_type === 'yacht_owner' && formData.yacht_length) {
        registerData.yacht_length = length_unit === 'feet'
          ? parseFloat(formData.yacht_length) * 0.3048
          : parseFloat(formData.yacht_length);
      }

      // Add helper-specific fields
      if (formData.user_type === 'helper') {
        registerData.department = formData.department;
        registerData.position = formData.position;
        registerData.experience_years = parseInt(formData.experience_years) || 0;
        registerData.certifications = formData.certifications;
        registerData.available_from = formData.available_from || null;
        registerData.preferred_sizes = formData.preferred_sizes;
        registerData.languages_spoken = formData.languages_spoken;
        registerData.date_of_birth = formData.date_of_birth || null;
        registerData.nationality = formData.nationality || null;
      }

      // Add T&C acceptance data
      registerData.tc_accepted = true;
      registerData.tc_accepted_at = new Date().toISOString();
      registerData.tc_version = '2026-03-24';

      const data = await register(registerData);
      const dashboardPath = data.user.user_type === 'helper' ? '/helper' : '/owner';
      navigate(dashboardPath);
    } catch (err) {
      // Error is handled by AuthContext
    } finally {
      setLoading(false);
    }
  };

  const handleYachtSubmit = (e) => {
    e.preventDefault();

    if (!formData.yacht_name.trim()) {
      setValidationError('Please enter your yacht name');
      return;
    }

    if (!formData.yacht_length || parseFloat(formData.yacht_length) <= 0) {
      setValidationError('Please enter a valid yacht length');
      return;
    }

    if (!formData.home_port) {
      setValidationError('Please select your home port');
      return;
    }

    if (!tcAccepted) {
      setValidationError('You must accept the Terms and Conditions to register');
      return;
    }

    handleFinalSubmit();
  };

  const handleHelperProfileSubmit = (e) => {
    e.preventDefault();
    if (!formData.department) {
      setValidationError('Please select your department');
      return;
    }
    if (!formData.position) {
      setValidationError('Please select your position');
      return;
    }
    if (!formData.experience_years && formData.experience_years !== 0) {
      setValidationError('Please enter your years of experience');
      return;
    }
    if (!formData.date_of_birth) {
      setValidationError('Please enter your date of birth');
      return;
    }
    if (!formData.nationality) {
      setValidationError('Please select your nationality');
      return;
    }
    if (!tcAccepted) {
      setValidationError('You must accept the Terms and Conditions to register');
      return;
    }
    if (socialData) {
      handleSocialComplete(e);
    } else {
      handleFinalSubmit();
    }
  };

  const displayError = error || validationError;

  // Google button component - only renders when client ID is available
  const GoogleButton = ({ label }) => {
    if (!GOOGLE_CLIENT_ID) return null;
    return (
      <GoogleLoginButtonInternal
        label={label}
        onSuccess={handleGoogleSuccess}
        onError={setValidationError}
        disabled={loading}
      />
    );
  };

  // Apple button temporarily disabled

  // Social phone completion step (after social sign-in, new user needs phone + maybe yacht)
  if (step === 'social-phone') {
    // If yacht owner, also ask for yacht details in same step
    const isYachtOwner = formData.user_type === 'yacht_owner';

    return (
      <div>
        <button
          onClick={() => { setStep(1); }}
          className="text-navy-600 hover:text-navy-800 mb-6 text-sm"
        >
          &larr; Back
        </button>

        <div className="text-center mb-6">
          <div className="h-14 w-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="h-7 w-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-navy-900">
            Welcome, {socialData.first_name}!
          </h1>
          <p className="text-navy-500 font-light mt-1">
            Just a few more details to get started
          </p>
        </div>

        {displayError && (
          <div className="mb-6 p-4 bg-red-50 rounded-apple flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
            <p className="text-red-700 text-sm">{displayError}</p>
          </div>
        )}

        <form onSubmit={isYachtOwner ? (e) => {
          e.preventDefault();
          if (!formData.phone.trim()) { setValidationError('Phone number is required'); return; }
          if (!formData.yacht_name.trim()) { setValidationError('Please enter your yacht name'); return; }
          if (!formData.yacht_length || parseFloat(formData.yacht_length) <= 0) { setValidationError('Please enter a valid yacht length'); return; }
          handleSocialComplete(e);
        } : handleSocialComplete} className="space-y-5">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-navy-700 mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="input-field"
              placeholder="+1 234 567 8900"
              required
            />
            <p className="mt-1 text-xs text-navy-500">Required for communication</p>

            {formData.user_type === 'helper' && (
              <label className="flex items-center mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="use_whatsapp"
                  checked={formData.use_whatsapp}
                  onChange={(e) => setFormData({ ...formData, use_whatsapp: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-navy-700">I use WhatsApp on this number</span>
              </label>
            )}
          </div>

          {isYachtOwner && (
            <>
              <div className="pt-2 border-t border-navy-200">
                <h3 className="text-lg font-semibold text-navy-900 mb-4 mt-4">Yacht Details</h3>
              </div>
              <div>
                <label htmlFor="yacht_name" className="block text-sm font-medium text-navy-700 mb-2">
                  Yacht Name
                </label>
                <input
                  type="text"
                  id="yacht_name"
                  name="yacht_name"
                  value={formData.yacht_name}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="e.g., Sea Breeze"
                  required
                />
              </div>
              <div>
                <label htmlFor="yacht_type" className="block text-sm font-medium text-navy-700 mb-2">
                  Yacht Type
                </label>
                <select
                  id="yacht_type"
                  name="yacht_type"
                  value={formData.yacht_type}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="motor">Motor Yacht</option>
                  <option value="sailing">Sailing Yacht</option>
                  <option value="catamaran">Catamaran</option>
                  <option value="superyacht">Superyacht</option>
                </select>
              </div>
              <div>
                <label htmlFor="yacht_length" className="block text-sm font-medium text-navy-700 mb-2">
                  Length (LOA)
                </label>
                <div className="flex gap-3">
                  <input
                    type="number"
                    id="yacht_length"
                    name="yacht_length"
                    value={formData.yacht_length}
                    onChange={handleChange}
                    className="input-field flex-1"
                    placeholder="e.g., 15"
                    min="1"
                    step="0.1"
                    required
                  />
                  <select
                    name="length_unit"
                    value={formData.length_unit}
                    onChange={handleChange}
                    className="input-field w-28"
                  >
                    <option value="meters">Meters</option>
                    <option value="feet">Feet</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Terms and Conditions */}
          <div className="border-t border-navy-200 pt-4">
            <TermsCheckbox
              checked={tcAccepted}
              onChange={setTcAccepted}
              onOpenTerms={() => setShowTcModal(true)}
            />
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading || !tcAccepted}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <span className="spinner h-5 w-5 mr-2" />
                  Creating account...
                </span>
              ) : (
                'Complete Registration'
              )}
            </button>
          </div>
        </form>

        {/* Terms Modal */}
        <TermsModal isOpen={showTcModal} onClose={() => setShowTcModal(false)} />
      </div>
    );
  }

  // Step 1: User Type Selection
  if (step === 1) {
    const inFreePeriod = isFreePeriod();

    return (
      <div>
        <div className="flex items-center justify-center mb-8">
          <img src="/logo.png" alt="1crew3" className="h-32 w-32 sm:h-36 sm:w-36 rounded-3xl" />
          <span className="ml-6 text-5xl sm:text-6xl font-bold text-navy-900 tracking-tight">
            1crew3
          </span>
        </div>

        {/* Free Registration Banner for Yacht Owners */}
        {inFreePeriod && !socialData && (
          <div className="mb-6">
            <CountdownTimer />
          </div>
        )}

        {socialData && (
          <div className="mb-6 p-4 bg-green-50 rounded-apple text-center">
            <p className="text-green-800 text-sm font-medium">
              Signed in as {socialData.email}
            </p>
            <p className="text-green-600 text-xs mt-1">Choose your account type below</p>
          </div>
        )}

        <h1 className="text-3xl font-bold text-navy-900 tracking-tight mb-2">
          Join 1crew3
        </h1>
        <p className="text-navy-500 font-light mb-6">
          How would you like to use 1crew3?
        </p>

        <div className="space-y-3">
          <button
            onClick={() => handleUserTypeSelect('yacht_owner')}
            className="w-full p-5 bg-white border border-navy-200 hover:border-primary-600 rounded-apple-lg text-left transition-all duration-300 group"
          >
            <div className="flex items-start">
              <div className="h-11 w-11 bg-navy-100 group-hover:bg-primary-50 rounded-apple flex items-center justify-center transition-colors duration-300">
                <Ship className="h-5 w-5 text-navy-600 group-hover:text-primary-600 transition-colors duration-300" />
              </div>
              <div className="ml-4 flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-medium text-navy-900">Yacht Owner / Captain</h3>
                  {inFreePeriod && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                      Free until June 1st
                    </span>
                  )}
                </div>
                <p className="text-navy-500 font-light mt-0.5 text-sm">
                  I need help with mooring my yacht when arriving at ports
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleUserTypeSelect('helper')}
            className="w-full p-5 bg-white border border-navy-200 hover:border-primary-600 rounded-apple-lg text-left transition-all duration-300 group"
          >
            <div className="flex items-start">
              <div className="h-11 w-11 bg-navy-100 group-hover:bg-primary-50 rounded-apple flex items-center justify-center transition-colors duration-300">
                <Users className="h-5 w-5 text-navy-600 group-hover:text-primary-600 transition-colors duration-300" />
              </div>
              <div className="ml-4 flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-medium text-navy-900">Crew / Helper</h3>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                    Always Free
                  </span>
                </div>
                <p className="text-navy-500 font-light mt-0.5 text-sm">
                  I want to assist yacht owners and earn tips
                </p>
              </div>
            </div>
          </button>
        </div>

        {!socialData && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-navy-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-navy-400 font-light">or</span>
              </div>
            </div>

            <GoogleButton label="Continue with Google" />
            <AppleButton label="Continue with Apple" />

            {displayError && (
              <div className="mt-4 p-4 bg-red-50 rounded-apple flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-red-700 text-sm">{displayError}</p>
              </div>
            )}
          </>
        )}

        <p className="mt-8 text-center text-navy-600">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    );
  }

  // Step 3 for helpers: Professional Profile
  if (step === 3 && formData.user_type === 'helper') {
    return (
      <>
        <HelperProfileStep
          formData={formData}
          handleChange={handleChange}
          handleHelperProfileSubmit={handleHelperProfileSubmit}
          toggleCertification={toggleCertification}
          toggleArrayItem={toggleArrayItem}
          setFormData={setFormData}
          setStep={setStep}
          loading={loading}
          displayError={displayError}
          validationError={validationError}
          setValidationError={setValidationError}
          tcAccepted={tcAccepted}
          setTcAccepted={setTcAccepted}
          onOpenTerms={() => setShowTcModal(true)}
        />
        <TermsModal isOpen={showTcModal} onClose={() => setShowTcModal(false)} />
      </>
    );
  }

  // Step 3 for owners: Yacht Details
  if (step === 3 && formData.user_type === 'yacht_owner') {
    return (
      <>
        <YachtDetailsStep
          formData={formData}
          handleChange={handleChange}
          handleYachtSubmit={handleYachtSubmit}
          setStep={setStep}
          loading={loading}
          displayError={displayError}
          tcAccepted={tcAccepted}
          setTcAccepted={setTcAccepted}
          onOpenTerms={() => setShowTcModal(true)}
        />
        <TermsModal isOpen={showTcModal} onClose={() => setShowTcModal(false)} />
      </>
    );
  }

  // Determine which SEO to use based on user type
  const seoConfig = formData.user_type === 'helper' ? PAGE_SEO.registerCrew : PAGE_SEO.registerOwner;

  // Step 2: Registration Form (email/password flow)
  return (
    <div>
      <SEOHead
        title={seoConfig.title}
        description={seoConfig.description}
        canonicalUrl="/register"
      />
      <button
        onClick={() => setStep(1)}
        className="text-navy-600 hover:text-navy-800 mb-6 text-sm"
      >
        &larr; Back to selection
      </button>

      <div className="flex items-center justify-center mb-6">
        <img src="/logo.png" alt="1crew3" className="h-32 w-32 sm:h-36 sm:w-36 rounded-3xl" />
        <span className="ml-6 text-5xl sm:text-6xl font-bold text-navy-900 tracking-tight">
          1crew3
        </span>
      </div>

      <h1 className="text-3xl font-bold tracking-tight text-navy-900 mb-2">
        Create your account
      </h1>
      <p className="text-navy-600 mb-6">
        {formData.user_type === 'helper'
          ? 'Join as a mooring helper'
          : 'Join as a yacht owner'}
      </p>

      {/* Social Sign-In */}
      <GoogleButton label="Sign up with Google" />
      <AppleButton label="Sign up with Apple" />

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-navy-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-navy-400 font-light">or register with email</span>
        </div>
      </div>

      {displayError && (
        <div className="mb-6 p-4 bg-red-50 rounded-apple flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
          <p className="text-red-700 text-sm">{displayError}</p>
        </div>
      )}

      <form onSubmit={handleAccountSubmit} className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="first_name" className="block text-sm font-medium text-navy-700 mb-2">
              First Name
            </label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className="input-field"
              placeholder="John"
              required
            />
          </div>
          <div>
            <label htmlFor="last_name" className="block text-sm font-medium text-navy-700 mb-2">
              Last Name
            </label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className="input-field"
              placeholder="Smith"
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-navy-700 mb-2">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="input-field"
            placeholder="you@example.com"
            required
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-navy-700 mb-2">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="input-field"
            placeholder="+1 234 567 8900"
            required
          />
          <p className="mt-1 text-xs text-navy-500">Required for communication with {formData.user_type === 'helper' ? 'yacht owners' : 'helpers'}</p>

          {formData.user_type === 'helper' && (
            <label className="flex items-center mt-3 cursor-pointer">
              <input
                type="checkbox"
                name="use_whatsapp"
                checked={formData.use_whatsapp}
                onChange={(e) => setFormData({ ...formData, use_whatsapp: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-navy-700">I use WhatsApp on this number</span>
            </label>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-navy-700 mb-2">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="input-field pr-12"
              placeholder="********"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-600"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          <p className="mt-1 text-xs text-navy-500">Minimum 8 characters</p>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-navy-700 mb-2">
            Confirm Password
          </label>
          <input
            type={showPassword ? 'text' : 'password'}
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className="input-field"
            placeholder="********"
            required
          />
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <span className="spinner h-5 w-5 mr-2" />
                Creating account...
              </span>
            ) : (
              'Create Account'
            )}
          </button>
        </div>

        <p className="text-xs text-navy-500 text-center">
          By creating an account, you agree to our{' '}
          <Link to="/terms" className="text-primary-600 hover:underline">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-primary-600 hover:underline">
            Privacy Policy
          </Link>
        </p>
      </form>

      <p className="mt-8 text-center text-navy-600">
        Already have an account?{' '}
        <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
          Sign in
        </Link>
      </p>
    </div>
  );
}

// Port Autocomplete Component - uses search API like helper service areas
function PortAutocomplete({ value, onChange, placeholder }) {
  const [inputValue, setInputValue] = useState(value || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (inputValue.length < 2) {
      setSearchResults([]);
      return;
    }

    const searchPorts = async () => {
      setSearching(true);
      try {
        const res = await fetch(`/api/ports?q=${encodeURIComponent(inputValue)}`);
        const data = await res.json();
        if (data.success) {
          setSearchResults(data.data || []);
        }
      } catch (err) {
        console.error('Port search failed:', err);
      } finally {
        setSearching(false);
      }
    };

    const debounce = setTimeout(searchPorts, 200);
    return () => clearTimeout(debounce);
  }, [inputValue]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
    setShowSuggestions(true);
  };

  const handleSelect = (port) => {
    setInputValue(port.name);
    onChange(port.name);
    setShowSuggestions(false);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        className="input-field"
        placeholder={placeholder || "Start typing port name..."}
        autoComplete="off"
      />
      {showSuggestions && searchResults.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-navy-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {searchResults.map(port => (
            <button
              key={port.id}
              type="button"
              onClick={() => handleSelect(port)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-navy-50 focus:bg-navy-50 transition-colors"
            >
              <span className="font-medium text-navy-900">{port.name}</span>
              {port.country && <span className="text-navy-500 ml-2">({port.country})</span>}
            </button>
          ))}
        </div>
      )}
      {searching && inputValue.length >= 2 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-navy-200 rounded-lg shadow-lg p-3 text-sm text-navy-500">
          Searching...
        </div>
      )}
    </div>
  );
}

// Step 3: Yacht Details (for yacht owners only)
function YachtDetailsStep({ formData, handleChange, handleYachtSubmit, setStep, loading, displayError, tcAccepted, setTcAccepted, onOpenTerms }) {
  return (
    <div>
      <button
        onClick={() => setStep(2)}
        className="text-navy-600 hover:text-navy-800 mb-6 text-sm"
      >
        &larr; Back to account details
      </button>

      <div className="flex items-center mb-6">
        <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center">
          <Anchor className="h-6 w-6 text-primary-600" />
        </div>
        <div className="ml-4">
          <h1 className="text-2xl font-bold tracking-tight text-navy-900">
            Your Yacht Details
          </h1>
          <p className="text-navy-600">
            Tell us about your yacht
          </p>
        </div>
      </div>

      {displayError && (
        <div className="mb-6 p-4 bg-red-50 rounded-apple flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
          <p className="text-red-700 text-sm">{displayError}</p>
        </div>
      )}

      <form onSubmit={handleYachtSubmit} className="space-y-5">
        <div>
          <label htmlFor="yacht_name" className="block text-sm font-medium text-navy-700 mb-2">
            Yacht Name
          </label>
          <input
            type="text"
            id="yacht_name"
            name="yacht_name"
            value={formData.yacht_name}
            onChange={handleChange}
            className="input-field"
            placeholder="e.g., Sea Breeze"
            required
          />
        </div>

        <div>
          <label htmlFor="yacht_type" className="block text-sm font-medium text-navy-700 mb-2">
            Yacht Type
          </label>
          <select
            id="yacht_type"
            name="yacht_type"
            value={formData.yacht_type}
            onChange={handleChange}
            className="input-field"
            required
          >
            <option value="motor">Motor Yacht</option>
            <option value="sailing">Sailing Yacht</option>
            <option value="catamaran">Catamaran</option>
            <option value="superyacht">Superyacht</option>
          </select>
        </div>

        <div>
          <label htmlFor="yacht_length" className="block text-sm font-medium text-navy-700 mb-2">
            Length (LOA)
          </label>
          <div className="flex gap-3">
            <input
              type="number"
              id="yacht_length"
              name="yacht_length"
              value={formData.yacht_length}
              onChange={handleChange}
              className="input-field flex-1"
              placeholder="e.g., 15"
              min="1"
              step="0.1"
              required
            />
            <select
              name="length_unit"
              value={formData.length_unit}
              onChange={handleChange}
              className="input-field w-28"
            >
              <option value="meters">Meters</option>
              <option value="feet">Feet</option>
            </select>
          </div>
          <p className="mt-1 text-xs text-navy-500">
            Length Overall - the total length of your yacht
          </p>
        </div>

        <div>
          <label htmlFor="home_port" className="block text-sm font-medium text-navy-700 mb-2">
            Home Port <span className="text-red-500">*</span>
          </label>
          <PortAutocomplete
            value={formData.home_port}
            onChange={(val) => handleChange({ target: { name: 'home_port', value: val } })}
            placeholder="Start typing port name..."
          />
          <p className="mt-1 text-xs text-navy-500">
            The port where your yacht is usually moored
          </p>
        </div>

        {/* Terms and Conditions */}
        <div className="border-t border-navy-200 pt-4">
          <TermsCheckbox
            checked={tcAccepted}
            onChange={setTcAccepted}
            onOpenTerms={onOpenTerms}
          />
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading || !tcAccepted}
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <span className="spinner h-5 w-5 mr-2" />
                Creating account...
              </span>
            ) : (
              'Complete Registration'
            )}
          </button>
        </div>

        <p className="text-xs text-navy-500 text-center">
          You can update your yacht details later in your profile
        </p>
      </form>
    </div>
  );
}

// Step 3: Helper Professional Profile (for helpers only)
function HelperProfileStep({ formData, handleChange, handleHelperProfileSubmit, toggleCertification, toggleArrayItem, setFormData, setStep, loading, displayError, validationError, setValidationError, tcAccepted, setTcAccepted, onOpenTerms }) {
  return (
    <div>
      <button onClick={() => setStep(2)} className="text-navy-600 hover:text-navy-800 mb-6 text-sm">
        &larr; Back to account details
      </button>

      <div className="flex items-center mb-6">
        <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center">
          <Anchor className="h-6 w-6 text-primary-600" />
        </div>
        <div className="ml-4">
          <h1 className="text-2xl font-bold tracking-tight text-navy-900">Your Professional Profile</h1>
          <p className="text-navy-600">Tell us about your yachting experience</p>
        </div>
      </div>

      {(displayError || validationError) && (
        <div className="mb-6 p-4 bg-red-50 rounded-apple flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
          <p className="text-red-700 text-sm">{displayError || validationError}</p>
        </div>
      )}

      <form onSubmit={handleHelperProfileSubmit} className="space-y-6">
        {/* Department Dropdown */}
        <div>
          <label htmlFor="department" className="block text-sm font-medium text-navy-700 mb-2">
            Department <span className="text-red-500">*</span>
          </label>
          <select
            id="department"
            name="department"
            value={formData.department}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, department: e.target.value, position: '', certifications: {} }));
              setValidationError('');
            }}
            className="input-field"
            required
          >
            <option value="">Select department...</option>
            {DEPARTMENTS.map(d => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>

        {/* Position Dropdown (filtered by department) */}
        {formData.department && (
          <div>
            <label htmlFor="position" className="block text-sm font-medium text-navy-700 mb-2">
              Position / Level <span className="text-red-500">*</span>
            </label>
            <select
              id="position"
              name="position"
              value={formData.position}
              onChange={handleChange}
              className="input-field"
              required
            >
              <option value="">Select position...</option>
              {(POSITIONS[formData.department] || []).map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Experience Years */}
        <div>
          <label htmlFor="experience_years" className="block text-sm font-medium text-navy-700 mb-2">
            Years of Yachting Experience <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            id="experience_years"
            name="experience_years"
            value={formData.experience_years}
            onChange={handleChange}
            className="input-field"
            placeholder="0"
            min="0"
            max="30"
            required
          />
        </div>

        {/* Date of Birth */}
        <div>
          <label htmlFor="date_of_birth" className="block text-sm font-medium text-navy-700 mb-2">
            Date of Birth <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            id="date_of_birth"
            name="date_of_birth"
            value={formData.date_of_birth}
            onChange={handleChange}
            className="input-field"
            max={new Date().toISOString().split('T')[0]}
            required
          />
          <p className="mt-1 text-xs text-navy-500">Required for work eligibility verification</p>
        </div>

        {/* Nationality */}
        <div>
          <label htmlFor="nationality" className="block text-sm font-medium text-navy-700 mb-2">
            Nationality (Passport) <span className="text-red-500">*</span>
          </label>
          <select
            id="nationality"
            name="nationality"
            value={formData.nationality}
            onChange={handleChange}
            className="input-field"
            required
          >
            <option value="">Select nationality...</option>
            <option value="AF">Afghan</option>
            <option value="AL">Albanian</option>
            <option value="DZ">Algerian</option>
            <option value="AR">Argentine</option>
            <option value="AU">Australian</option>
            <option value="AT">Austrian</option>
            <option value="BE">Belgian</option>
            <option value="BR">Brazilian</option>
            <option value="BG">Bulgarian</option>
            <option value="CA">Canadian</option>
            <option value="CL">Chilean</option>
            <option value="CN">Chinese</option>
            <option value="CO">Colombian</option>
            <option value="HR">Croatian</option>
            <option value="CY">Cypriot</option>
            <option value="CZ">Czech</option>
            <option value="DK">Danish</option>
            <option value="NL">Dutch</option>
            <option value="EG">Egyptian</option>
            <option value="EE">Estonian</option>
            <option value="FI">Finnish</option>
            <option value="FR">French</option>
            <option value="DE">German</option>
            <option value="GR">Greek</option>
            <option value="HU">Hungarian</option>
            <option value="IN">Indian</option>
            <option value="ID">Indonesian</option>
            <option value="IE">Irish</option>
            <option value="IL">Israeli</option>
            <option value="IT">Italian</option>
            <option value="JP">Japanese</option>
            <option value="LV">Latvian</option>
            <option value="LT">Lithuanian</option>
            <option value="MY">Malaysian</option>
            <option value="MT">Maltese</option>
            <option value="MX">Mexican</option>
            <option value="MC">Monegasque</option>
            <option value="ME">Montenegrin</option>
            <option value="MA">Moroccan</option>
            <option value="NZ">New Zealander</option>
            <option value="NO">Norwegian</option>
            <option value="PH">Filipino</option>
            <option value="PL">Polish</option>
            <option value="PT">Portuguese</option>
            <option value="RO">Romanian</option>
            <option value="RU">Russian</option>
            <option value="SA">Saudi</option>
            <option value="RS">Serbian</option>
            <option value="SG">Singaporean</option>
            <option value="SK">Slovak</option>
            <option value="SI">Slovenian</option>
            <option value="ZA">South African</option>
            <option value="KR">South Korean</option>
            <option value="ES">Spanish</option>
            <option value="SE">Swedish</option>
            <option value="CH">Swiss</option>
            <option value="TH">Thai</option>
            <option value="TR">Turkish</option>
            <option value="UA">Ukrainian</option>
            <option value="AE">Emirati</option>
            <option value="GB">British</option>
            <option value="US">American</option>
            <option value="OTHER">Other</option>
          </select>
          <p className="mt-1 text-xs text-navy-500">Country of your passport</p>
        </div>

        {/* Certifications - only shown after position is selected */}
        {/* Shows ONLY the necessary/expected certs for the selected position */}
        {formData.position && (
        <div>
          <h3 className="text-base font-semibold text-navy-900 mb-3">Required Certifications</h3>
          <p className="text-sm text-navy-500 mb-4">
            These certifications are typically required for <span className="font-medium">{(POSITIONS[formData.department] || []).find(p => p.value === formData.position)?.label || formData.position}</span>.
            <br />Tick the ones you already have.
          </p>
          <div className="space-y-2">
            {(POSITION_EXPECTED_CERTS[formData.position] || []).map(certId => {
              const cert = CERTIFICATION_GROUPS.flatMap(g => g.certs).find(c => c.id === certId);
              if (!cert) return null;
              return (
                <label key={cert.id} className="flex items-center cursor-pointer py-2.5 px-3 rounded-lg bg-navy-50 hover:bg-navy-100 border border-navy-200">
                  <input
                    type="checkbox"
                    checked={!!formData.certifications[cert.id]}
                    onChange={() => toggleCertification(cert.id)}
                    className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-navy-900 font-medium">{cert.label}</span>
                </label>
              );
            })}
          </div>
          {(POSITION_EXPECTED_CERTS[formData.position] || []).length === 0 && (
            <p className="text-sm text-navy-500 italic">No specific certifications required for this position.</p>
          )}
        </div>
        )}

        {/* Divider */}
        <div className="border-t border-navy-200 pt-4">
          <h3 className="text-base font-semibold text-navy-900 mb-3">Preferences (optional)</h3>
        </div>

        {/* Available From */}
        <div>
          <label htmlFor="available_from" className="block text-sm font-medium text-navy-700 mb-2">
            Available From
          </label>
          <input
            type="date"
            id="available_from"
            name="available_from"
            value={formData.available_from}
            onChange={handleChange}
            className="input-field"
          />
        </div>

        {/* Preferred Vessel Size */}
        <div>
          <label className="block text-sm font-medium text-navy-700 mb-2">Preferred Vessel Size</label>
          <div className="space-y-2">
            {VESSEL_SIZES.map(size => (
              <label key={size.value} className="flex items-center cursor-pointer py-1">
                <input
                  type="checkbox"
                  checked={formData.preferred_sizes.includes(size.value)}
                  onChange={() => toggleArrayItem('preferred_sizes', size.value)}
                  className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-3 text-sm text-navy-700">{size.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Languages */}
        <div>
          <label className="block text-sm font-medium text-navy-700 mb-2">Languages Spoken</label>
          <div className="grid grid-cols-2 gap-2">
            {LANGUAGES.map(lang => (
              <label key={lang.value} className="flex items-center cursor-pointer py-1">
                <input
                  type="checkbox"
                  checked={formData.languages_spoken.includes(lang.value)}
                  onChange={() => toggleArrayItem('languages_spoken', lang.value)}
                  className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-3 text-sm text-navy-700">{lang.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Terms and Conditions */}
        <div className="border-t border-navy-200 pt-4">
          <TermsCheckbox
            checked={tcAccepted}
            onChange={setTcAccepted}
            onOpenTerms={onOpenTerms}
          />
        </div>

        {/* Submit */}
        <div className="pt-4">
          <button type="submit" disabled={loading || !tcAccepted} className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? (
              <span className="flex items-center justify-center">
                <span className="spinner h-5 w-5 mr-2" />
                Creating account...
              </span>
            ) : (
              'Complete Registration'
            )}
          </button>
        </div>

        <p className="text-xs text-navy-500 text-center">
          You can update your profile and certifications later
        </p>
      </form>
    </div>
  );
}

export default RegisterPage;
